import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Styled Components
const SectionWrapper = styled.section`
  padding: 100px 0;
  background-color: var(--bg-white);
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CaseStudiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const CaseStudyCard = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const CaseStudyImage = styled.div`
  flex: 1;
  min-height: 200px;
  background-color: var(--bg-light);
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (min-width: 768px) {
    max-width: 40%;
  }
`;

const CaseStudyContent = styled.div`
  flex: 1.5;
  padding: 2rem;
`;

const CaseStudyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: var(--primary);
`;

const CaseStudyType = styled.p`
  font-size: 0.875rem;
  color: var(--text-light);
  margin-bottom: 1rem;
`;

const CaseStudyDescription = styled.p`
  color: var(--text-dark);
  margin-bottom: 1.5rem;
`;

const MetricsContainer = styled.div`
  border-top: 1px solid var(--bg-light);
  padding-top: 1.5rem;
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MetricLabel = styled.span`
  color: var(--text-light);
`;

const MetricValue = styled.span`
  color: var(--success);
  font-weight: 600;
`;

const ActionButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary);
  color: white;
  padding: 0.75rem 1.5rem;
  margin-top: 2rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  text-align: center;
  transition: all var(--transition-normal);
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
    color: white;
  }
`;

// SVG Components for case studies
const VegetableUniversityLogo = () => (
    <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Circle background */}
        <circle cx="100" cy="100" r="90" fill="#E9F7EF" />

        {/* Vegetable illustrations */}
        <g transform="translate(50, 40)">
            {/* Carrot */}
            <path d="M30,10 C35,15 40,25 40,40 C40,60 35,80 30,100 C25,80 20,60 20,40 C20,25 25,15 30,10 Z" fill="#F59E0B" />
            <path d="M30,10 C25,5 15,5 15,15 C15,25 20,25 25,25 C25,15 30,10 30,10 Z" fill="#10B981" />
            <path d="M30,10 C35,5 45,5 45,15 C45,25 40,25 35,25 C35,15 30,10 30,10 Z" fill="#10B981" />
            <path d="M38,15 C40,12 45,10 50,15 C55,20 50,25 45,25 C42,20 38,15 38,15 Z" fill="#10B981" />
        </g>

        {/* University hat */}
        <rect x="75" y="50" width="50" height="10" fill="#111827" />
        <rect x="85" y="30" width="30" height="20" fill="#111827" />
        <rect x="95" y="20" width="10" height="10" fill="#111827" />
        <path d="M75,60 L100,75 L125,60" stroke="#111827" strokeWidth="2" fill="none" />

        {/* Text circle */}
        <circle cx="100" cy="100" r="90" stroke="#10B981" strokeWidth="3" fill="none" />
    </svg>
);

const LuxStyleLogo = () => (
    <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Background */}
        <rect x="10" y="10" width="180" height="180" rx="20" fill="#F5F3FF" />

        {/* LUX text */}
        <text x="100" y="90" fontFamily="Arial" fontSize="40" fontWeight="bold" fill="#8B5CF6" textAnchor="middle">LUX</text>
        <text x="100" y="130" fontFamily="Arial" fontSize="24" fontWeight="normal" fill="#4B5563" textAnchor="middle">STYLE</text>

        {/* Decorative elements */}
        <rect x="40" y="40" width="120" height="120" rx="10" stroke="#8B5CF6" strokeWidth="2" fill="none" />
        <path d="M40,70 L160,70" stroke="#8B5CF6" strokeWidth="2" />
        <path d="M40,140 L160,140" stroke="#8B5CF6" strokeWidth="2" />

        {/* Fashion icon */}
        <circle cx="100" y="40" r="15" fill="#8B5CF6" />
        <path d="M95,35 L105,45 M95,45 L105,35" stroke="white" strokeWidth="2" />
    </svg>
);

// CaseStudies Component
const CaseStudies = () => {
    return (
        <SectionWrapper id="case-studies">
            <SectionContainer>
                <SectionHeader>
                    <SectionTitle>成功案例展示</SectionTitle>
                    <SectionDescription>
                        看看我們如何幫助不同產業的客戶打造成功的行動購物體驗
                    </SectionDescription>
                </SectionHeader>

                <CaseStudiesGrid>
                    {/* 蔬果大學案例 */}
                    <CaseStudyCard>
                        <CaseStudyImage>
                            <VegetableUniversityLogo />
                        </CaseStudyImage>
                        <CaseStudyContent>
                            <CaseStudyTitle>蔬果大學</CaseStudyTitle>
                            <CaseStudyType>有機食品 & 生鮮配送</CaseStudyType>
                            <CaseStudyDescription>
                                蔬果大學是一家專注於有機蔬果配送的電商平台，透過 ShopWave 的解決方案，他們建立了直覺易用的行動應用，讓顧客能夠輕鬆選購新鮮食材。
                            </CaseStudyDescription>
                            <MetricsContainer>
                                <MetricRow>
                                    <MetricLabel>訂單成長率</MetricLabel>
                                    <MetricValue>↑ 76%</MetricValue>
                                </MetricRow>
                                <MetricRow>
                                    <MetricLabel>客戶留存率</MetricLabel>
                                    <MetricValue>↑ 45%</MetricValue>
                                </MetricRow>
                            </MetricsContainer>
                        </CaseStudyContent>
                    </CaseStudyCard>

                    {/* LuxStyle案例 */}
                    <CaseStudyCard>
                        <CaseStudyImage>
                            <LuxStyleLogo />
                        </CaseStudyImage>
                        <CaseStudyContent>
                            <CaseStudyTitle>LuxStyle</CaseStudyTitle>
                            <CaseStudyType>精品服飾 & 配件</CaseStudyType>
                            <CaseStudyDescription>
                                LuxStyle 是一家高端時尚精品店，他們利用 ShopWave 打造出符合品牌調性的購物應用，呈現精緻的視覺體驗和順暢的購物流程。
                            </CaseStudyDescription>
                            <MetricsContainer>
                                <MetricRow>
                                    <MetricLabel>平均訂單金額</MetricLabel>
                                    <MetricValue>↑ 32%</MetricValue>
                                </MetricRow>
                                <MetricRow>
                                    <MetricLabel>轉換率</MetricLabel>
                                    <MetricValue>↑ 28%</MetricValue>
                                </MetricRow>
                            </MetricsContainer>
                        </CaseStudyContent>
                    </CaseStudyCard>
                </CaseStudiesGrid>

                <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                    <ActionButton to="/contact">查看更多案例</ActionButton>
                </div>
            </SectionContainer>
        </SectionWrapper>
    );
};

export default CaseStudies;